import { chooseEntity, idOf, spokenMoney } from "../ownerJarvisUtils";


function asksPendings(text = "") {
  const hasPendingWord = /\b(?:pendientes?|tareas?|acciones?)\b/.test(text);
  const asksToReview = /\b(?:ver|mostr\w*|mir\w*|abr\w*|list\w*|revis\w*|leer|leeme|léeme|cont\w*|cuant\w*)\b/.test(text)
    || /\b(?:que|qué|cuales|cuáles)\s+(?:pendientes?|tareas?|acciones?)\b/.test(text);
  return hasPendingWord && asksToReview;
}

function isNewProjectPaymentStatement(text = "") {
  const paid = /\bme\s+(?:acaba\s+de\s+|acabo\s+de\s+)?pag(?:o|aron|ar)\b|\brecien\s+me\s+pago\b|\b(?:ya\s+)?cobre\b/.test(text);
  const amount = /\b\d[\d.,]*\b/.test(text);
  const stateQuestion = /\bcuant(?:o|a|os|as)\s+me\s+pago\b|\bfalta\s+cobrar\b|\bcomo\s+va\b/.test(text);
  const recurring = /\b(?:mensualidad|mensual|recurrente|suscripcion)\b/.test(text);
  return paid && amount && !stateQuestion && !recurring;
}

function explicitlyScopesPendingsToProject(text = "") {
  if (/\b(?:proyecto|frente|iniciativa)\b/.test(text)) return true;
  const match = text.match(/\b(?:pendientes?|tareas?|acciones?)\s+(?:de|del|de la)\s+(.+)$/);
  if (!match?.[1]) return false;
  const tail = String(match[1] || "").trim();
  return Boolean(tail) && !/^(?:todos?|todas?|mis|los proyectos|todos los proyectos|proyectos|hoy|ahora)$/.test(tail);
}

function projectPendingState(step = {}) {
  const state = String(step?.work?.state || "ready");
  if (state === "in_progress") return "en marcha";
  if (state === "waiting") {
    const waitingFor = String(step?.work?.waitingFor || "").trim();
    return waitingFor ? `esperando ${waitingFor}` : "esperando";
  }
  if (state === "blocked") {
    const reason = String(step?.work?.blockedReason || step?.work?.blockedBy || "").trim();
    return reason ? `trabado por ${reason}` : "trabado";
  }
  return "listo para avanzar";
}

function pendingTypeLabel(step = {}) {
  const labels = { action: "tarea", fix: "fix", meeting: "reunión", objective: "objetivo", idea: "idea", note: "nota" };
  return labels[String(step?.projectItemType || "action")] || "pendiente";
}

function spokenProjectPendings(projectName, rows = []) {
  const pending = (rows || []).filter((step) => String(step?.status || "open") === "open");
  if (!pending.length) return `${projectName} no tiene pendientes abiertos.`;
  const detail = pending.map((step, index) => {
    const title = String(step?.title || "Pendiente sin título").trim();
    return `${index + 1}, ${pendingTypeLabel(step)}: ${title}, ${projectPendingState(step)}`;
  });
  return `${projectName} tiene ${pending.length} ${pending.length === 1 ? "pendiente" : "pendientes"}. ${detail.join(". ")}.`;
}

function spokenAllProjectPendings(commitments = [], initiatives = []) {
  const projectById = new Map((initiatives || []).map((row) => [idOf(row), row]).filter(([id]) => Boolean(id)));
  const projectRows = (commitments || []).filter((row) => row?.initiativeId && row?.kind === "project_next_step" && String(row?.status || "open") === "open");
  if (!projectRows.length) return "No tenés pendientes abiertos vinculados a proyectos.";

  const grouped = new Map();
  projectRows.forEach((row) => {
    const initiativeId = idOf(row?.initiativeId);
    if (!initiativeId) return;
    if (!grouped.has(initiativeId)) grouped.set(initiativeId, []);
    grouped.get(initiativeId).push(row);
  });

  const groups = [...grouped.entries()]
    .map(([initiativeId, rows]) => ({
      initiativeId,
      name: String(projectById.get(initiativeId)?.name || "Proyecto sin nombre"),
      rows,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "es"));

  const parts = groups.map((group) => {
    const items = group.rows.map((row, index) => `${index + 1}, ${String(row?.title || "Pendiente sin título").trim()}, ${projectPendingState(row)}`);
    return `${group.name}: ${group.rows.length} ${group.rows.length === 1 ? "pendiente" : "pendientes"}. ${items.join(". ")}`;
  });
  return `Tenés ${projectRows.length} ${projectRows.length === 1 ? "pendiente" : "pendientes"} abiertos en ${groups.length} ${groups.length === 1 ? "proyecto" : "proyectos"}. ${parts.join(". ")}. Decime “ver pendientes de” y el nombre de un proyecto para abrirlos y operar sobre ellos.`;
}

function openProjectPendingsModal(initiative) {
  const initiativeId = idOf(initiative);
  if (!initiativeId || typeof window === "undefined") return false;
  try {
    window.dispatchEvent(new CustomEvent("owner:open-project-pendings", {
      detail: {
        initiativeId,
        projectName: String(initiative?.name || ""),
        token: `project-pendings:${initiativeId}:${Date.now()}`,
      },
    }));
    return true;
  } catch {
    return false;
  }
}

function spokenAgreementMoney(rows = {}) {
  const parts = Object.entries(rows || {})
    .map(([currency, value]) => [currency, Number((value?.amount ?? value) || 0)])
    .filter(([, value]) => value > 0)
    .map(([currency, value]) => spokenMoney(value, currency));
  return parts.length ? parts.join(" y ") : "cero";
}

export function createOwnerJarvisProjectCapability({ ownerApi, navigate }) {
  return {
    id: "project-control",
    async handle({ text }) {
      if (asksPendings(text)) {
        const [initiativesResponse, commitmentsResponse] = await Promise.all([
          ownerApi.listInitiatives({ status: "", limit: 100 }),
          ownerApi.listCommitments({ status: "open", limit: 200 }),
        ]);
        const initiatives = initiativesResponse?.initiatives || [];
        const commitments = commitmentsResponse?.commitments || [];
        const result = chooseEntity(initiatives, text, ["name", "outcome", "notes"]);

        if (result.row) {
          const initiativeId = idOf(result.row);
          const contextResponse = await ownerApi.initiativeContext(initiativeId);
          const context = contextResponse?.context || {};
          const steps = context?.steps?.items || context?.commitments?.items || commitments.filter((row) => idOf(row?.initiativeId) === initiativeId);
          const spoken = spokenProjectPendings(result.row.name, steps);
          const opened = openProjectPendingsModal(result.row);
          if (!opened) {
            navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`, {
              state: { ownerJarvisUi: { surface: "project360", initiativeId, section: "project-work", token: `project-pendings-fallback:${initiativeId}:${Date.now()}` } },
            });
            return { handled: true, message: `${spoken} Te abro el trabajo del proyecto.` };
          }
          return { handled: true, message: `${spoken} Te los dejo abiertos acá. Podés decirme qué querés hacer o usar el modal para crear, editar, empezar, completar u ordenar.` };
        }

        if (result.alternatives?.length) {
          return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row, index) => `${index + 1}, ${row.name}`).join("; ")}. Decime cuál y te leo sus pendientes.` };
        }

        if (explicitlyScopesPendingsToProject(text)) {
          return { handled: true, message: "No encontré ese proyecto. Decime el nombre como aparece en Proyectos y te leo sus pendientes." };
        }

        return { handled: true, message: spokenAllProjectPendings(commitments, initiatives) };
      }

      const asksMissingSteps = /\b(?:que|qué)\s+(?:falta|queda|resta)\b/.test(text) || /\b(?:falta|queda|resta)\s+(?:para|de)\b/.test(text);
      const asksBlockedSteps = /\b(?:bloquead[oa]s?|bloquea|bloqueo|trabad[oa]s?|traba|impide avanzar)\b/.test(text);
      if (asksMissingSteps || asksBlockedSteps) {
        const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
        const result = chooseEntity(response?.initiatives || [], text, ["name", "outcome", "notes"]);
        if (result.row) {
          const initiativeId = idOf(result.row);
          const contextResponse = await ownerApi.initiativeContext(initiativeId);
          const context = contextResponse?.context || {};
          const steps = context?.steps?.items || [];
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
          if (!steps.length) return { handled: true, message: `${result.row.name} todavía no tiene pasos estructurados. No voy a inventar qué falta; te abro el proyecto para cargarlos.` };
          const pending = steps.filter((step) => step.status === "open");
          const done = steps.filter((step) => step.status === "done").length;
          if (asksBlockedSteps) {
            const blocked = pending.filter((step) => String(step?.work?.state || "ready") === "blocked");
            if (!blocked.length) return { handled: true, message: `${result.row.name} no tiene bloqueos operativos estructurados abiertos. No voy a inferir una traba desde notas.` };
            const visibleBlocked = blocked.slice(0, 5).map((step) => {
              const reason = String(step?.work?.blockedReason || step?.work?.blockedBy || "").trim() || "bloqueo sin motivo detallado";
              const unblock = String(step?.work?.unblockAction || "").trim();
              return `${step.title}: ${reason}${unblock ? `. Para destrabar: ${unblock}` : ""}`;
            });
            const caution = blocked.some((step) => !String(step?.work?.unblockAction || "").trim()) ? " En los demás casos no voy a inventar cómo destrabarlos." : "";
            return { handled: true, message: `${result.row.name} tiene ${blocked.length} ${blocked.length === 1 ? "bloqueo" : "bloqueos"}: ${visibleBlocked.join("; ")}.${caution}` };
          }
          if (!pending.length) return { handled: true, message: `${result.row.name}: los ${steps.length} pasos registrados están hechos. Si todavía falta algo, agregalo como un paso real del proyecto.` };
          const label = (step) => {
            const code = String(step?.executability?.code || "");
            if (code.startsWith("dependency_") && step?.executability?.dependency?.title) return `no está disponible porque primero falta ${step.executability.dependency.title}`;
            const state = String(step?.work?.state || "ready");
            if (state === "waiting") return step?.work?.waitingFor ? `esperando ${step.work.waitingFor}` : "esperando";
            if (state === "blocked") {
              const reason = step?.work?.blockedReason || step?.work?.blockedBy || "";
              return reason ? `trabado por ${reason}` : "trabado";
            }
            if (state === "in_progress") return "en marcha";
            return step?.executability?.executable === false ? "todavía no ejecutable" : "listo";
          };
          const visible = pending.slice(0, 5).map((step, index) => `${index + 1}, ${step.title} (${label(step)}${step.estimatedMinutes ? `, aprox. ${step.estimatedMinutes} min` : ""})`);
          const more = pending.length > visible.length ? ` y ${pending.length - visible.length} más` : "";
          const next = context?.nextAction?.executable && context?.nextAction?.title ? ` Ahora podés avanzar con ${context.nextAction.title}.` : "";
          return { handled: true, message: `${result.row.name}: ${done} de ${steps.length} pasos hechos. Faltan ${pending.length}: ${visible.join("; ")}${more}.${next}` };
        }
        if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row, index) => `${index + 1}, ${row.name}`).join("; ")}. Decime el nombre más completo.` };
      }

      const asksProjectControl = !isNewProjectPaymentStatement(text)
        && /\b(?:como va|cuanto|cuanta|pagado|pago|pagaron|presupuesto|falta cobrar|falta pagar|pendiente)\b/.test(text)
        && /\b(?:proyecto|me pago|me pagaron|cliente|presupuesto|falta cobrar|como va)\b/.test(text);
      if (asksProjectControl) {
        const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
        const result = chooseEntity(response?.initiatives || [], text, ["name", "outcome", "notes"]);
        if (result.row) {
          const initiativeId = idOf(result.row);
          const [agreementResponse, productResponse] = await Promise.all([
            ownerApi.projectAgreement(initiativeId),
            ownerApi.initiativeProductHealth(initiativeId, { days: 30 }).catch(() => ({ health: null })),
          ]);
          const agreement = agreementResponse?.agreement || {};
          const summary = agreement.summary || {};
          const health = productResponse?.health || null;
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
          const parts = [`${result.row.name}: cobrado ${spokenAgreementMoney(summary.paidByCurrency)}.`];
          if (Object.keys(summary.knownBudgetByCurrency || {}).length) parts.push(`Presupuesto cerrado conocido ${spokenAgreementMoney(summary.knownBudgetByCurrency)}; pendiente ${spokenAgreementMoney(summary.outstandingByCurrency)}.`);
          const usdTerm = (agreement.terms || []).find((term) => term.currency === "USD" && term.outstanding > 0 && term.fx?.outstandingArsNow);
          if (usdTerm) parts.push(`Del saldo en dólares faltan ${spokenMoney(usdTerm.outstanding, "USD")}; con la cotización ${usdTerm.fx.quote || "configurada"} de hoy son aproximadamente ${spokenMoney(usdTerm.fx.outstandingArsNow, "ARS")}. Lo que ya cobraste queda congelado y no se recalcula.`);
          if (Object.keys(summary.recurringMonthlyByCurrency || {}).length) parts.push(`Además tiene ${spokenAgreementMoney(summary.recurringMonthlyByCurrency)} mensuales recurrentes.`);
          if (summary.taskProgress?.total) parts.push(`Checklist ${summary.taskProgress.done} de ${summary.taskProgress.total} tareas completas.`);
          if (health?.linked) {
            const stageLabels = { implementation: "en implementación", delivered: "entregado", operating: "operando", inactive: "inactivo" };
            parts.push(`El WebProfit está ${stageLabels[health.adoption?.stage] || "vinculado"}, con adopción ${Math.round(Number(health.adoption?.score || 0))} por ciento.`);
            parts.push(`En los últimos ${health.period?.days || 30} días tuvo ${health.storefront?.pageViews || 0} visitas, ${health.storefront?.contactClicks || 0} contactos y ${health.storefront?.cartAdds || 0} agregados al carrito.`);
          }
          parts.push("Te abro la ficha para ver contrato, avance y uso real del producto.");
          return { handled: true, message: parts.join(" ") };
        }
        if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row, index) => `${index + 1}, ${row.name}`).join("; ")}. Decime el nombre más completo.` };
      }

      const asksWebProfitProduct = /\b(?:webprofit|pagina|web|visitas|visitantes|contactos|whatsapp|carrito|carritos|checkout|uso|adopcion|ultimo uso|ultima edicion)\b/.test(text)
        && /\b(?:proyecto|cliente|webprofit|pagina|visitas|contactos|carritos|uso|adopcion|como va|estado)\b/.test(text);
      if (!asksWebProfitProduct) return null;

      const response = await ownerApi.listInitiatives({ status: "", limit: 120 });
      const result = chooseEntity(response?.initiatives || [], text, ["name", "outcome", "notes"]);
      if (result.row) {
        const initiativeId = idOf(result.row);
        const productResponse = await ownerApi.initiativeProductHealth(initiativeId, { days: 30 });
        const health = productResponse?.health || null;
        navigate(`/dashboard/owner/proyectos/${encodeURIComponent(initiativeId)}`);
        if (!health?.linked) return { handled: true, message: `${result.row.name} todavía no tiene un Project WebProfit vinculado. Te abro la ficha para vincularlo.` };
        const stageLabels = { implementation: "en implementación", delivered: "entregado", operating: "operando", inactive: "inactivo" };
        const parts = [`${result.row.name}: WebProfit ${stageLabels[health.adoption?.stage] || "vinculado"}, adopción ${Math.round(Number(health.adoption?.score || 0))} por ciento.`];
        parts.push(`Últimos ${health.period?.days || 30} días: ${health.storefront?.pageViews || 0} visitas, ${health.storefront?.contactClicks || 0} contactos, ${health.storefront?.cartAdds || 0} carritos y ${health.orders?.orders || 0} pedidos.`);
        if (health.panel?.landingEdits) parts.push(`La página se guardó ${health.panel.landingEdits} veces en ese período.`);
        parts.push("Te abro la ficha con la evidencia.");
        return { handled: true, message: parts.join(" ") };
      }
      if (result.alternatives?.length) return { handled: true, message: `Encontré varios proyectos parecidos: ${result.alternatives.map((row, index) => `${index + 1}, ${row.name}`).join("; ")}. Decime el nombre más completo.` };
      return null;
    },
  };
}
